import React,{ useState,useEffect, useMemo, useRef } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector} from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Button,
  Menu,
  MenuItem,
  FormControl,
  Select,
} from '@mui/material';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DiscountIcon from '@mui/icons-material/Discount';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function Earningreports() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const tableRef = useRef(null);

  const settings = useSelector(state => state.settingsdata.settings);

  const columns =  [
      { title: t('period'), field: 'monthsName',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarMonthIcon sx={{ color: '#2563EB', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                {rowData.monthsName || '-'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                {rowData.year}
              </Typography>
            </Box>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('booking_count'), field: 'total_rides',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#6B7280', fontSize: 16 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              {rowData.total_rides || 0}
            </Typography>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('Gross_trip_cost'), field: 'tripCost',
        render: rowData => {
          const grossCost = (parseFloat(rowData.tripCost) + parseFloat(rowData.cancellationFee)).toFixed(settings.decimal || 2);
          return (
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
              {settings.swipe_symbol ? `${grossCost}${settings.symbol}` : `${settings.symbol}${grossCost}`}
            </Typography>
          );
        },
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('Discounts'), field: 'discountAmount',
        render: rowData => (
          <Chip
            label={settings.swipe_symbol ? `${rowData.discountAmount || 0}${settings.symbol}` : `${settings.symbol}${rowData.discountAmount || 0}`}
            size="small"
            sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 600, fontSize: 12 }}
          />
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('Profit'), field: 'profit',
        render: rowData => {
          const profit = (parseFloat(rowData.convenienceFee) + parseFloat(rowData.cancellationFee) - parseFloat(rowData.discountAmount)).toFixed(settings.decimal || 2);
          const isPositive = parseFloat(profit) >= 0;
          return (
            <Chip
              label={settings.swipe_symbol ? `${profit}${settings.symbol}` : `${settings.symbol}${profit}`}
              size="small"
              sx={{
                bgcolor: isPositive ? '#DCFCE7' : '#FEE2E2',
                color: isPositive ? '#16A34A' : '#DC2626',
                fontWeight: 600,
                fontSize: 13,
                height: 28,
              }}
            />
          );
        },
        cellStyle: { padding: '14px 16px' },
      },
  ];

  const [data, setData] = useState([]);
  const earningreportsdata = useSelector(state => state.earningreportsdata);

  useEffect(()=>{
        if(earningreportsdata.Earningreportss){
            setData(earningreportsdata.Earningreportss);
        }
  },[earningreportsdata.Earningreportss]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter states
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Export menu
  const [exportAnchor, setExportAnchor] = useState(null);

  // Get unique years for filters
  const filterOptions = useMemo(() => {
    const years = [...new Set(data.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);
    return { years };
  }, [data]);

  // Filtered data with all filters applied
  const filteredData = useMemo(() => {
    let result = data;

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.monthsName && d.monthsName.toLowerCase().includes(query)) ||
        (d.year && String(d.year).includes(query))
      );
    }

    // Month filter
    if (selectedMonth) {
      result = result.filter(d => d.monthsName === selectedMonth);
    }

    // Year filter
    if (selectedYear) {
      result = result.filter(d => d.year === parseInt(selectedYear));
    }

    return result;
  }, [data, searchQuery, selectedMonth, selectedYear]);

  // Summary calculations based on filtered data
  const summaryData = useMemo(() => {
    const totalBookings = filteredData.reduce((sum, d) => sum + (parseInt(d.total_rides) || 0), 0);
    const grossRevenue = filteredData.reduce((sum, d) => sum + (parseFloat(d.tripCost) || 0) + (parseFloat(d.cancellationFee) || 0), 0);
    const totalProfit = filteredData.reduce((sum, d) => sum + (parseFloat(d.convenienceFee) || 0) + (parseFloat(d.cancellationFee) || 0) - (parseFloat(d.discountAmount) || 0), 0);
    const totalDiscounts = filteredData.reduce((sum, d) => sum + (parseFloat(d.discountAmount) || 0), 0);
    return { totalBookings, grossRevenue, totalProfit, totalDiscounts };
  }, [filteredData]);

  // Check if any filter is active
  const hasActiveFilters = selectedMonth || selectedYear;

  // Clear all filters
  const clearFilters = () => {
    setSelectedMonth('');
    setSelectedYear('');
    setSearchQuery('');
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      t('year'), t('months'), t('booking_count'), t('Gross_trip_cost'),
      t('trip_cost_driver_share'), t('convenience_fee'), t('cancellationFee'),
      t('Discounts'), t('Profit')
    ];
    const csvData = filteredData.map(d => {
      const grossCost = (parseFloat(d.tripCost) + parseFloat(d.cancellationFee)).toFixed(settings.decimal || 2);
      const profit = (parseFloat(d.convenienceFee) + parseFloat(d.cancellationFee) - parseFloat(d.discountAmount)).toFixed(settings.decimal || 2);
      return [
        d.year || '',
        d.monthsName || '',
        d.total_rides || 0,
        grossCost,
        d.rideCost || 0,
        d.convenienceFee || 0,
        d.cancellationFee || 0,
        d.discountAmount || 0,
        profit
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `earning_reports_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  };

  // Export to PDF
  const exportToPDF = () => {
    const tableHTML = `
      <html>
        <head>
          <title>${t('earning_reports') || 'Earning Reports'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #111827; font-size: 24px; margin-bottom: 10px; }
            .subtitle { color: #6B7280; margin-bottom: 20px; }
            .summary { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
            .summary-card { padding: 15px; border: 1px solid #E5E7EB; border-radius: 8px; min-width: 150px; }
            .summary-label { color: #6B7280; font-size: 12px; }
            .summary-value { font-size: 20px; font-weight: bold; color: #111827; }
            .summary-value.profit { color: ${summaryData.totalProfit >= 0 ? '#16A34A' : '#DC2626'}; }
            .summary-value.discount { color: #D97706; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #F3F4F6; padding: 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #E5E7EB; font-size: 12px; }
            td { padding: 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; }
            .profit-positive { color: #16A34A; font-weight: 600; }
            .profit-negative { color: #DC2626; font-weight: 600; }
            .footer { margin-top: 30px; color: #6B7280; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${t('earning_reports') || 'Earning Reports'}</h1>
          <p class="subtitle">${t('generated_on') || 'Generated on'}: ${dayjs().format('MMMM D, YYYY')}</p>
          <div class="summary">
            <div class="summary-card">
              <div class="summary-label">${t('total_bookings') || 'Total Bookings'}</div>
              <div class="summary-value">${summaryData.totalBookings}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">${t('gross_revenue') || 'Gross Revenue'}</div>
              <div class="summary-value">${settings.symbol}${summaryData.grossRevenue.toFixed(2)}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">${t('total_profit') || 'Total Profit'}</div>
              <div class="summary-value profit">${settings.symbol}${summaryData.totalProfit.toFixed(2)}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">${t('total_discounts') || 'Total Discounts'}</div>
              <div class="summary-value discount">${settings.symbol}${summaryData.totalDiscounts.toFixed(2)}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>${t('period')}</th>
                <th>${t('booking_count')}</th>
                <th>${t('Gross_trip_cost')}</th>
                <th>${t('trip_cost_driver_share')}</th>
                <th>${t('convenience_fee')}</th>
                <th>${t('Discounts')}</th>
                <th>${t('Profit')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(d => {
                const grossCost = (parseFloat(d.tripCost) + parseFloat(d.cancellationFee)).toFixed(settings.decimal || 2);
                const profit = (parseFloat(d.convenienceFee) + parseFloat(d.cancellationFee) - parseFloat(d.discountAmount)).toFixed(settings.decimal || 2);
                const profitClass = parseFloat(profit) >= 0 ? 'profit-positive' : 'profit-negative';
                return `
                  <tr>
                    <td>${d.monthsName || '-'} ${d.year || ''}</td>
                    <td>${d.total_rides || 0}</td>
                    <td>${settings.symbol}${grossCost}</td>
                    <td>${settings.symbol}${d.rideCost || 0}</td>
                    <td>${settings.symbol}${d.convenienceFee || 0}</td>
                    <td>${settings.symbol}${d.discountAmount || 0}</td>
                    <td class="${profitClass}">${settings.symbol}${profit}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="footer">
            ${t('total_records') || 'Total Records'}: ${filteredData.length}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.print();
    setExportAnchor(null);
  };

  return (
    earningreportsdata.loading? <CircularLoading/>:
    <Box style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('earning_reports') || 'Earning Reports'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('earning_reports_description') || 'Monthly revenue and profit analysis'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={{
              bgcolor: '#111827',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              '&:hover': { bgcolor: '#374151' },
            }}
          >
            {t('export') || 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150 } }}
          >
            <MenuItem onClick={exportToCSV} sx={{ gap: 1.5 }}>
              <FileDownloadIcon sx={{ fontSize: 18, color: '#16A34A' }} />
              {t('export_csv') || 'Export CSV'}
            </MenuItem>
            <MenuItem onClick={exportToPDF} sx={{ gap: 1.5 }}>
              <PictureAsPdfIcon sx={{ fontSize: 18, color: '#DC2626' }} />
              {t('export_pdf') || 'Export PDF'}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon sx={{ color: '#2563EB', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_bookings') || 'Total Bookings'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalBookings}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AttachMoneyIcon sx={{ color: '#9333EA', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('gross_revenue') || 'Gross Revenue'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {settings.swipe_symbol ? `${summaryData.grossRevenue.toFixed(settings.decimal || 2)}${settings.symbol}` : `${settings.symbol}${summaryData.grossRevenue.toFixed(settings.decimal || 2)}`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUpIcon sx={{ color: '#16A34A', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_profit') || 'Total Profit'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: summaryData.totalProfit >= 0 ? '#16A34A' : '#DC2626' }}>
              {settings.swipe_symbol ? `${summaryData.totalProfit.toFixed(settings.decimal || 2)}${settings.symbol}` : `${settings.symbol}${summaryData.totalProfit.toFixed(settings.decimal || 2)}`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DiscountIcon sx={{ color: '#D97706', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_discounts') || 'Total Discounts'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#D97706' }}>
              {settings.swipe_symbol ? `${summaryData.totalDiscounts.toFixed(settings.decimal || 2)}${settings.symbol}` : `${settings.symbol}${summaryData.totalDiscounts.toFixed(settings.decimal || 2)}`}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F9FAFB',
            },
          }}
        />

        {/* Year Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="">{t('year') || 'Year'}</MenuItem>
            {filterOptions.years.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Month Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="">{t('month') || 'Month'}</MenuItem>
            {MONTHS.map(month => (
              <MenuItem key={month} value={month}>{month}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={clearFilters}
            sx={{
              color: '#DC2626',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { bgcolor: '#FEE2E2' },
            }}
          >
            {t('clear') || 'Clear'}
          </Button>
        )}

        {/* Results Count */}
        <Typography sx={{ fontSize: 13, color: '#6B7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{filteredData.length}</strong> {t('results') || 'results'}
        </Typography>
      </Box>

    <MaterialTable
      tableRef={tableRef}
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        exportButton: false,
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      detailPanel={rowData => {
        const grossCost = (parseFloat(rowData.tripCost) + parseFloat(rowData.cancellationFee)).toFixed(settings.decimal || 2);
        const profit = (parseFloat(rowData.convenienceFee) + parseFloat(rowData.cancellationFee) - parseFloat(rowData.discountAmount)).toFixed(settings.decimal || 2);
        return (
          <Box sx={{ p: 3, bgcolor: '#F9FAFB' }}>
            <Grid container spacing={3}>
              {/* Revenue Details */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  {t('revenue_details') || 'Revenue Details'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('trip_cost') || 'Trip Cost'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {settings.swipe_symbol ? `${rowData.tripCost}${settings.symbol}` : `${settings.symbol}${rowData.tripCost}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('trip_cost_driver_share') || 'Driver Share'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {settings.swipe_symbol ? `${rowData.rideCost}${settings.symbol}` : `${settings.symbol}${rowData.rideCost}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('Gross_trip_cost') || 'Gross Cost'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#2563EB' }}>
                      {settings.swipe_symbol ? `${grossCost}${settings.symbol}` : `${settings.symbol}${grossCost}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* Profit Details */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  {t('profit_details') || 'Profit Details'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('convenience_fee') || 'Convenience Fee'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {settings.swipe_symbol ? `${rowData.convenienceFee}${settings.symbol}` : `${settings.symbol}${rowData.convenienceFee}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('cancellationFee') || 'Cancellation Fee'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {settings.swipe_symbol ? `${rowData.cancellationFee}${settings.symbol}` : `${settings.symbol}${rowData.cancellationFee}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('Discounts') || 'Discounts'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#D97706' }}>
                      -{settings.swipe_symbol ? `${rowData.discountAmount}${settings.symbol}` : `${settings.symbol}${rowData.discountAmount}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #E5E7EB' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{t('net_profit') || 'Net Profit'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: parseFloat(profit) >= 0 ? '#16A34A' : '#DC2626' }}>
                      {settings.swipe_symbol ? `${profit}${settings.symbol}` : `${settings.symbol}${profit}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* Period Info */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  {t('period_info') || 'Period Info'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('year') || 'Year'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.year || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('months') || 'Month'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.monthsName || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('booking_count') || 'Bookings'}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.total_rides || 0}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      }}
      localization={{
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        pagination: {
          labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
          firstTooltip: (t('first_page_tooltip')),
          previousTooltip: (t('previous_page_tooltip')),
          nextTooltip: (t('next_page_tooltip')),
          lastTooltip: (t('last_page_tooltip'))
        },
      }}

    />
    </Box>
  );
}
